import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { Form, Formik } from "formik";
import * as yup from "yup";
import ms_applicant from "../../validators/ms_applicant";
import ErrorMessage from "../../components/ErrorMessage";
import useAuthStore from "../../stores/useAuthStore";

const resetPasswordRules = yup.object({
  otp: yup.string().required("OTP code is required"),
  password: yup
    .string()
    .min(8, "Minimum of 8 characters expected")
    .required("New password is required"),
  confirmpassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your new password"),
});

const Forgot = () => {
  const navigate = useNavigate();
  const { settings } = useAuthStore();
  const [step, setStep] = useState(1);
  const [emailSent, setEmailSent] = useState("");

  const _requestOtp = async (values) => {
    try {
      const req = await axiospost("ms_students/forgot", {
        emailaddress: values.emailaddress,
      });
      if (req?.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { type, message } = req.data;
      if (type === "success") {
        swal(type, message);
        setEmailSent(values.emailaddress);
        setStep(2);
      } else {
        swal(type || "warning", message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  const _resetPassword = async (values) => {
    try {
      const req = await axiospost("ms_students/reset-password", {
        emailaddress: emailSent,
        otp: values.otp,
        password: values.password,
      });
      if (req?.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { type, message } = req.data;
      if (type === "success") {
        swal(type, message);
        setTimeout(() => navigate("/"), 1500);
      } else {
        swal(type || "warning", message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Image Section */}
      <div
        className="hidden lg:flex lg:flex-[0_0_65%] flex-col justify-center items-center text-white p-10 text-center"
        style={{
          background: "linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45)), url('/images/classroom.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <h1 className="text-5xl font-extrabold mb-5">{settings?.institutionName || settings?.shortName || "ATBCOE Kangere"}</h1>
        <p className="text-lg opacity-90">Recover your account password to regain access to the portal.</p>
      </div>

      {/* Form Section */}
      <div className="flex flex-col justify-center items-center p-10 bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.05)] flex-[0_0_35%] max-lg:flex-[0_0_100%]">
        <div className="flex justify-center mb-8 mt-16">
          <img src={settings?.logo || "/images/college-logo.png"} alt="Institution Logo" className="h-20" onError={(e) => { e.target.onerror = null; e.target.src = "/images/college-logo.png"; }} />
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>1</div>
          <div className={`h-1 w-10 rounded ${step >= 2 ? "bg-blue-600" : "bg-gray-200"}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"}`}>2</div>
        </div>

        {step === 1 && (
          <>
            <div className="text-center mb-4">
              <h4 style={{ fontWeight: 700 }}>RECOVER PASSWORD</h4>
              <p className="text-muted">Enter your email address to receive an OTP</p>
            </div>
            <div className="w-full max-w-[400px]">
              <Formik
                initialValues={{ emailaddress: "" }}
                validationSchema={ms_applicant.forgetpasswordrules}
                onSubmit={_requestOtp}
              >
                {({ isSubmitting, touched, errors }) => (
                  <Form>
                    <div className="mb-4">
                      <ErrorMessage name="emailaddress" label="Email Address" errors={errors?.emailaddress} touched={touched?.emailaddress} />
                    </div>
                    <button type="submit" disabled={isSubmitting} className="btn btn-primary w-100 py-3 font-weight-bold">
                      {isSubmitting ? "Please wait..." : "Send OTP"}
                    </button>
                    <div className="mt-4 text-center">
                      <Link to="/" className="text-decoration-none font-weight-bold">Back to Login</Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="text-center mb-4">
              <h4 style={{ fontWeight: 700 }}>RESET PASSWORD</h4>
              <p className="text-muted">Enter the OTP sent to <strong>{emailSent}</strong> and your new password</p>
            </div>
            <div className="w-full max-w-[400px]">
              <Formik
                initialValues={{ otp: "", password: "", confirmpassword: "" }}
                validationSchema={resetPasswordRules}
                onSubmit={_resetPassword}
              >
                {({ isSubmitting, touched, errors }) => (
                  <Form>
                    <div className="mb-3">
                      <ErrorMessage name="otp" label="OTP Code" errors={errors?.otp} touched={touched?.otp} />
                    </div>
                    <div className="mb-3">
                      <ErrorMessage name="password" label="New Password" type="password" errors={errors?.password} touched={touched?.password} />
                    </div>
                    <div className="mb-4">
                      <ErrorMessage name="confirmpassword" label="Confirm New Password" type="password" errors={errors?.confirmpassword} touched={touched?.confirmpassword} />
                    </div>
                    <button type="submit" disabled={isSubmitting} className="btn btn-primary w-100 py-3 font-weight-bold">
                      {isSubmitting ? "Please wait..." : "Reset Password"}
                    </button>
                    <div className="mt-4 text-center">
                      <button type="button" className="btn btn-link p-0 font-weight-bold" onClick={() => setStep(1)}>
                        Back
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Forgot;
